import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { groupsMemberRequestApproveDecline } from '@wix/bi-logger-groups/v2';

import type { IJoinRequest } from 'api/groups/types';
import { selectJoinRequestStatus } from 'store/selectors';

import { Button } from 'wui/Button';
import { Show } from 'wui/Show';
import { Hide } from 'wui/Hide';
import { Menu, MenuItem } from 'wui/Menu';
import { ButtonGroup } from 'wui/ButtonGroup';
import { IconButton } from 'wui/IconButton';
import { More as MoreIcon } from '@wix/wix-ui-icons-common/on-stage';

import { useRelativeTimeFormatter } from 'common/hooks';
import { useController } from 'common/context/controller';
import { ProfileListItem } from 'common/components/ProfileListItem';

import { RejectDialog } from './RejectDialog';
import { ReviewDialog } from './ReviewDialog';

interface IJoinRequestProps {
  groupId: string;
  request: IJoinRequest;
}

export function JoinRequest(props: IJoinRequestProps) {
  const { request, groupId } = props;

  const bi = useBi();
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const rtf = useRelativeTimeFormatter();
  const { members$ } = useController();
  const status = useSelector(
    selectJoinRequestStatus(request.profile.siteMemberId as string),
  );

  const [dialogs, setDialogs] = useState({
    review: false,
    reject: false,
  });

  const hasAnswers = !!request.answers?.length;

  return (
    <>
      <ProfileListItem
        disablePadding
        profile={request.profile}
        subtitle={rtf(request.createdDate as Date)}
      >
        <Show if={isMobile}>
          <Menu
            element={
              <IconButton
                icon={<MoreIcon />}
                aria-label={t('groups-web.a11y.more-actions')}
              />
            }
          >
            <MenuItem
              onClick={handleOpenRejectDialog}
              content={t('groups-web.members.pending.decline')}
            />
            {hasAnswers ? (
              <MenuItem
                onClick={handleOpenReviewDialog}
                content={t('groups-web.members.pending.review')}
              />
            ) : (
              <MenuItem
                onClick={handleApprove}
                content={t('groups-web.members.pending.approve')}
              />
            )}
          </Menu>
        </Show>
        <Hide if={isMobile}>
          <ButtonGroup>
            <Button outlined onClick={handleOpenRejectDialog}>
              {t('groups-web.members.pending.decline')}
            </Button>
            <Show if={hasAnswers}>
              <Button onClick={handleOpenReviewDialog}>
                {t('groups-web.members.pending.review')}
              </Button>
            </Show>
            <Hide if={hasAnswers}>
              <Button
                onClick={handleApprove}
                disabled={status.approve.loading}
                loading={status.approve.loading}
              >
                {t('groups-web.members.pending.approve')}
              </Button>
            </Hide>
          </ButtonGroup>
        </Hide>
      </ProfileListItem>

      <ReviewDialog
        groupId={groupId}
        isOpen={dialogs.review}
        request={request}
        onClose={handleCloseReviewDialog}
        onReject={handleOpenRejectDialog}
      />

      <RejectDialog
        groupId={groupId}
        isOpen={dialogs.reject}
        profile={request.profile}
        onClose={handleCloseRejectDialog}
      />
    </>
  );

  function handleApprove() {
    members$.approve(groupId, request.profile.siteMemberId as string);
    bi.report(
      groupsMemberRequestApproveDecline({
        groupId,
        isAll: false,
        memberId: request.profile.siteMemberId,
        origin: 'user_lvl_btn',
      }),
    );
  }

  function handleOpenRejectDialog() {
    setDialogs((dialogs) => ({ ...dialogs, reject: true }));
  }

  function handleCloseRejectDialog() {
    setDialogs((dialogs) => ({ ...dialogs, reject: false }));
  }

  function handleOpenReviewDialog() {
    setDialogs((dialogs) => ({ ...dialogs, review: true }));
  }

  function handleCloseReviewDialog() {
    setDialogs((dialogs) => ({ ...dialogs, review: false }));
  }
}

JoinRequest.displayName = 'JoinRequest';
